<template>
  <v-container fluid class="planning">
    <YearHeader
      :year="planning.year"
      :disable-previous="currentYearIndex <= 0"
      :disable-next="currentYearIndex >= availableYears.length - 1"
      @previous="changeYear(-1)"
      @next="changeYear(1)"
    />
    <SummaryCards :summary="planning.summary" />

    <v-row class="mt-4">
      <v-col
        v-for="quarter in planning.quarters"
        :key="quarter.name"
        cols="12"
        md="3"
      >
        <QuarterColumn :quarter="quarter" />
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
import { mapGetters } from "vuex";
import YearHeader from "@/components/planning/YearHeader.vue";
import SummaryCards from "@/components/planning/SummaryCards.vue";
import QuarterColumn from "@/components/planning/QuarterColumn.vue";
import api from "@/api/api";

// How far out (in years) recurring actions get projected from their base date.
const OCCURRENCE_HORIZON_YEARS = 12;

export default {
  components: { YearHeader, SummaryCards, QuarterColumn },

  props: {
    installation_id: {
      type: Array,
      required: true,
    },
  },

  data() {
    return {
      loading: false,
      rawPlanning: [],
      planning: {
        year: new Date().getFullYear(),
        summary: { actions: 0, totalCost: 0, totalProfit: 0 },
        quarters: [1, 2, 3, 4].map((q) => ({
          id: q,
          name: `T${q}`,
          totalCost: 0,
          totalProfit: 0,
          actions: [],
        })),
      },
    };
  },

  computed: {
    ...mapGetters({ installations: "installations" }),

    // Every occurrence (original + repeats out to the horizon) across all
    // rows, flattened. Single source of truth for both which years are
    // navigable and which actions land in each quarter.
    allOccurrences() {
      const occurrences = [];

      this.rawPlanning.forEach((row) => {
        occurrences.push(
          ...this.buildOccurrencesForRow({
            row,
            dateField: "next_cleaning_date",
            lastDatesField: "cleaning_dates",
            costField: "cleaning_cost",
            gainField: "gain_cleaning_client",
            freqField: "freq_cleaning_client",
            type: "nettoyage",
            label: "Nettoyage",
          })
        );

        occurrences.push(
          ...this.buildOccurrencesForRow({
            row,
            dateField: "next_pruning_date",
            lastDatesField: "pruning_dates",
            costField: "pruning_cost",
            gainField: "gain_pruning_client",
            freqField: "freq_pruning_client",
            type: "elagage",
            label: "Élagage",
          })
        );
      });

      return occurrences;
    },

    // Sorted, deduplicated list of navigable years: every year that has at
    // least one action, PLUS the real current year even if it has none —
    // so the view always has somewhere sensible to land by default.
    availableYears() {
      const years = new Set(
        this.allOccurrences.map((o) => new Date(o.date).getFullYear())
      );
      years.add(new Date().getFullYear());
      return [...years].sort((a, b) => a - b);
    },

    minYear() {
      return this.availableYears.length
        ? this.availableYears[0]
        : this.planning.year;
    },

    maxYear() {
      return this.availableYears.length
        ? this.availableYears[this.availableYears.length - 1]
        : this.planning.year;
    },

    currentYearIndex() {
      return this.availableYears.indexOf(this.planning.year);
    },
  },

  watch: {
    installation_id: {
      immediate: true,
      handler(newVal) {
        if (newVal && newVal.length) this.fetchPlanning();
      },
    },
  },

  methods: {
    getSiteName(id) {
      const site = this.installations.find(
        (i) => Number(i.installation_id) === Number(id)
      );
      return site ? site.installation_name : `Site ${id}`;
    },

    getQuarter(dateStr) {
      return Math.floor(new Date(dateStr).getMonth() / 3) + 1;
    },

    // "6M" -> 6, "24M" -> 24. Returns null for empty / "no clean" / anything non-numeric.
    parseFreqMonths(freq) {
      if (!freq) return null;
      const match = /^(\d+)\s*M$/i.exec(String(freq).trim());
      return match ? parseInt(match[1], 10) : null;
    },

    addMonths(dateStr, months) {
      const d = new Date(dateStr);
      d.setMonth(d.getMonth() + months);
      return d.toISOString().slice(0, 10);
    },

    formatMonthYear(dateStr) {
      const d = new Date(dateStr);
      return `${String(d.getMonth() + 1).padStart(2, "0")}/${d.getFullYear()}`;
    },

    // Builds occurrences for one row/type: the given base date, then repeats
    // every `freqMonths` out to OCCURRENCE_HORIZON_YEARS years past that base
    // date. No frequency (empty / "no clean" / unparseable) -> single card only.
    buildOccurrencesForRow({
      row,
      dateField,
      lastDatesField,
      costField,
      gainField,
      freqField,
      type,
      label,
    }) {
      const baseDate = row[dateField];
      const lastDates = row[lastDatesField] || [];

      const freqMonths = this.parseFreqMonths(row[freqField]);
      const siteName = this.getSiteName(row.installation_id);
      const gain = this.getNumericValue(row[gainField]);
      const cost = this.getNumericValue(row[costField]);

      const occurrences = [];

      // --------------------------------------------------
      // Past / realised
      // Create ONE card for EVERY completed action
      // --------------------------------------------------
      lastDates.forEach((lastDate, index) => {
        occurrences.push({
          id: `${type}-${row.installation_id}-last-${index}`,
          type,
          title: `${label} ${siteName}`,
          date: lastDate,
          lastDate: lastDate,
          cost,
          gain: 0,
          freq: null,
          isPast: true,
        });
      });

      // Most recent completed date
      const latestLastDate = lastDates.length
        ? lastDates[lastDates.length - 1]
        : null;

      // --------------------------------------------------
      // Next / current planning, repeated out to the horizon
      // --------------------------------------------------
      if (baseDate) {
        const horizonEndDate = this.addMonths(
          baseDate,
          OCCURRENCE_HORIZON_YEARS * 12
        );

        let currentDate = baseDate;
        let index = 0;

        while (new Date(currentDate) <= new Date(horizonEndDate)) {
          occurrences.push({
            id: `${type}-${row.installation_id}-occ-${index}`,
            type,
            title: `${label} ${siteName}`,
            date: currentDate,
            lastDate: latestLastDate,
            cost,
            gain,
            freq: row[freqField],
            isPast: false,
          });

          index += 1;

          // No frequency -> single occurrence only, same as before.
          if (!freqMonths) break;

          currentDate = this.addMonths(currentDate, freqMonths);
        }
      }

      return occurrences;
    },

    getNumericValue(value) {
      if (value == null) return 0;

      if (typeof value === "number") {
        return value;
      }

      if (typeof value === "object" && value.parsedValue != null) {
        return Number(value.parsedValue);
      }

      return Number(value) || 0;
    },

    async fetchPlanning() {
      this.loading = true;
      try {
        const response = await api.getCleaningPlanning(this.installation_id);
        this.rawPlanning = response.data || [];
      } catch (error) {
        console.error("Failed to fetch cleaning planning:", error);
        this.rawPlanning = [];
      } finally {
        this.loading = false;

        // Default to the real current year whenever it's navigable (it
        // always is now, per availableYears), otherwise fall back to the
        // earliest year that actually has data.
        const currentYear = new Date().getFullYear();
        this.planning.year = this.availableYears.includes(currentYear)
          ? currentYear
          : this.minYear;

        this.buildQuarters();
      }
    },

    buildQuarters() {
      const year = this.planning.year;
      const quarters = [1, 2, 3, 4].map((q) => ({
        id: q,
        name: `T${q} ${year}`,
        totalCost: 0,
        totalProfit: 0,
        actions: [],
      }));

      this.allOccurrences
        .filter((o) => new Date(o.date).getFullYear() === year)
        .forEach((o) => {
          const q = this.getQuarter(o.date);
          const action = {
            id: o.id,
            type: o.type,
            title: o.title,
            dateLabel: this.formatMonthYear(o.date),
            date: o.date,
            lastDate: o.lastDate,
            cost: Math.round((o.cost / 100) * 100),
            gain: Math.round((o.gain / 100) * 100),
            freq: o.freq,
            isPast: o.isPast,
          };
          quarters[q - 1].actions.push(action);
          quarters[q - 1].totalCost += Math.round((action.cost / 100) * 100);
          quarters[q - 1].totalProfit += Math.round((action.gain / 100) * 100);
        });

      quarters.forEach((quarter) => {
        quarter.actions.sort((a, b) => {
          if (b.gain !== a.gain) return b.gain - a.gain;
          return new Date(a.date) - new Date(b.date);
        });
      });

      // 2. "Réalisé" — from the separate historical-dates source (once wired up)
      // this.previousPlanning would need its own fetch — shape TBD pending your answer above

      this.planning.quarters = quarters;
      this.planning.summary = {
        actions: quarters.reduce((sum, q) => sum + q.actions.length, 0),

        totalCost:
          Math.round(quarters.reduce((sum, q) => sum + q.totalCost, 0) / 100) *
          100,

        totalProfit:
          Math.round(
            quarters.reduce((sum, q) => sum + q.totalProfit, 0) / 100
          ) * 100,
      };
    },

    // Jumps to the previous/next YEAR THAT HAS DATA, skipping empty years entirely.
    changeYear(delta) {
      const newIndex = this.currentYearIndex + delta;
      if (newIndex < 0 || newIndex >= this.availableYears.length) return;
      this.planning.year = this.availableYears[newIndex];
      this.buildQuarters();
    },
  },
};
</script>